<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import ProcessService, { TaskErrorLog } from '@/modules/low-code/process/services/ProcessService';

@Options({
    name: 'task-error-modal'
})
export default class TaskErrorModal extends Vue
{
    public currentErrorIndex = 0;
    public errors: TaskErrorLog[] = [];
    public loading = false;
    public currentHistoryId: number | null = null;

    @Ref('modal')
    public modal: () => IdeoModal;

    public get currentError(): TaskErrorLog | null
    {
        return this.errors[this.currentErrorIndex] || null;
    }

    public get hasMultipleErrors(): boolean
    {
        return this.errors.length > 1;
    }

    public get canGoPrevious(): boolean
    {
        return this.currentErrorIndex > 0;
    }

    public get canGoNext(): boolean
    {
        return this.currentErrorIndex < this.errors.length - 1;
    }

    public previousError(): void
    {
        if (this.canGoPrevious)
        {
            this.currentErrorIndex--;
        }
    }

    public nextError(): void
    {
        if (this.canGoNext)
        {
            this.currentErrorIndex++;
        }
    }

    public close(): void
    {
        this.currentErrorIndex = 0;
        this.errors = [];
        this.currentHistoryId = null;
    }

    public async loadErrors(historyId: number): Promise<void>
    {
        this.loading = true;
        this.errors = [];
        this.currentErrorIndex = 0;
        this.currentHistoryId = historyId;

        try
        {
            this.errors = await ProcessService.getHistoryProcessLogs(this.currentHistoryId);
        }
        catch (error)
        {
            this.handleException(error, {
                0: () => this.$alert.danger(this.$t('[[[Nie udało się pobrać logów procesu]]]')),
            });
            this.errors = [];
        }
        finally
        {
            this.loading = false;
        }
    }

    public async openWithHistoryId(historyId: number): Promise<void>
    {
        this.modal().show();
        await this.loadErrors(historyId);
    }
}
</script>

<template>
    <IdeoModal
        ref="modal"
        :title="$t('[[[Błędy podczas wykonywania akcji]]]')"
        hide-footer
        size="lg"
        @close="close"
    >
        <div v-if="loading" class="text-center py-4 justify-center">
            <IdeoSpinner />
            <p class="mt-2">{{ $t('[[[Ładowanie błędów...]]]') }}</p>
        </div>

        <div v-else-if="currentError" class="task-error-content">
            <div class="error-source mb-3">
                <h6 class="text-info mb-2">
                    <i class="fas fa-map-marker-alt me-2"></i>
                    {{ $t('[[[Źródło błędu]]]') }}
                </h6>
                <p class="mb-0 fw-bold">{{ currentError.source }}</p>
            </div>

            <div class="error-message mb-3">
                <h6 class="text-danger mb-2">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    {{ $t('[[[Komunikat błędu]]]') }}
                </h6>
                <p class="mb-0">{{ currentError.message }}</p>
            </div>

            <div class="error-stack-trace">
                <h6 class="text-danger mb-2">
                    <i class="fas fa-code me-2"></i>
                    {{ $t('[[[Stack trace]]]') }}
                </h6>
                <pre><code>{{ currentError.stackTrace }}</code></pre>
            </div>

            <div v-if="hasMultipleErrors" class="error-navigation mt-4">
                <div class="d-flex justify-content-center align-items-center gap-3">
                    <IdeoButton
                        variant="secondary"
                        size="sm"
                        :disabled="!canGoPrevious"
                        @click="previousError"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </IdeoButton>

                    <span class="error-counter">
                        {{ currentErrorIndex + 1 }} / {{ errors.length }}
                    </span>

                    <IdeoButton
                        variant="secondary"
                        size="sm"
                        :disabled="!canGoNext"
                        @click="nextError"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </IdeoButton>
                </div>
            </div>
        </div>

        <div v-else-if="!loading && errors.length === 0" class="text-center py-4">
            <i class="fas fa-info-circle text-muted" style="font-size: 2rem;"></i>
            <p class="mt-2 text-muted">{{ $t('[[[Brak błędów do wyświetlenia]]]') }}</p>
        </div>
    </IdeoModal>
</template>

<style lang="scss" scoped>
.task-error-content {
    .error-source {
        background-color: var(--bs-info-bg-subtle);
        border: 1px solid var(--bs-info-border-subtle);
        border-radius: 0.375rem;
        padding: 1rem;
    }

    .error-message {
        background-color: var(--bs-danger-bg-subtle);
        border: 1px solid var(--bs-danger-border-subtle);
        border-radius: 0.375rem;
        padding: 1rem;
    }

    .error-stack-trace {
        pre {
            background-color: var(--bs-gray-100);
            border: 1px solid var(--bs-gray-300);
            border-radius: 0.375rem;
            padding: 1rem;
            margin: 0;
            font-size: 0.875rem;
            line-height: 1.5;
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
        }

        code {
            color: var(--bs-gray-700);
            font-family: 'Courier New', Courier, monospace;
        }
    }

    .error-navigation {
        .error-counter {
            font-weight: 600;
            color: var(--bs-gray-600);
            min-width: 60px;
            text-align: center;
        }
    }
}
</style>
